
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui/types/element-ui'

import StepStructure from './components/StepStructure.vue'
import StepBankCards from './components/StepBankCards.vue'
import StepValidation from './components/StepValidation.vue'
import RgpdConsentDialog from './components/RgpdConsentDialog.vue'
import EnrollmentSuccessDialog from './components/EnrollmentSuccessDialog.vue'
import {
  createEmptyEnrollmentForm,
  createEmptyAddress,
  EnrollmentForm,
  EnrollmentResult,
  getDemoPartnerPrefill,
  PartnerPrefillData
} from './types/enrollmentForm'
import { buildStructureRules, buildDeliveryAddressRules } from './utils/validation'
import { removeJwtFromUrl, usePlanetbEnrollment } from './utils/usePlanetbEnrollment'

@Component({
  name: 'Enrollment',
  components: {
    StepStructure,
    StepBankCards,
    StepValidation,
    RgpdConsentDialog,
    EnrollmentSuccessDialog
  }
})
export default class extends Vue {
  form: EnrollmentForm = createEmptyEnrollmentForm()
  step = 0
  submitting = false
  legalError = false
  showRgpdDialog = false
  successVisible = false
  prefilledFields: string[] = []
  partnerPrefillAvailable = false
  demoPrefill: PartnerPrefillData = getDemoPartnerPrefill()
  enrollmentResult: EnrollmentResult = { carrefourProId: '', passAvantageId: '' }
  planetbLoading = false
  planetbPrefillSuccess = false

  get structureRules() {
    return {
      ...buildStructureRules(
        (key: string) => this.$t(key) as string,
        () => this.form
      ),
      ...this.dynamicAddressRules
    }
  }

  get dynamicAddressRules() {
    const t = (key: string) => this.$t(key) as string
    if (this.needsPhysicalAddressValidation) {
      return buildDeliveryAddressRules(t, 'physicalDeliveryAddresses', this.form.physicalDeliveryAddresses)
    }
    return {}
  }

  get needsPhysicalAddressValidation(): boolean {
    return this.form.wantsPhysicalCards && this.form.deliveryAddresses.length === 0
  }

  async created(): Promise<void> {
    const jwt = this.$route.query.jwt
    if (typeof jwt === 'string' && jwt.trim()) {
      const token = jwt
      const { jwt: _jwt, ...queryWithoutJwt } = this.$route.query
      removeJwtFromUrl()
      await this.$router.replace({ query: queryWithoutJwt })
      await this.loadPlanetbEnrollment(token)
      return
    }

    this.partnerPrefillAvailable = this.$route.query.prefill === 'demo'
    if (this.partnerPrefillAvailable) {
      this.showRgpdDialog = true
    }
  }

  async loadPlanetbEnrollment(jwt: string): Promise<void> {
    this.planetbLoading = true
    try {
      const result = await usePlanetbEnrollment(
        jwt,
        this.form,
        (key: string) => this.$t(key) as string
      )

      if (result.status === 'success') {
        this.prefilledFields = result.prefilledFields
        this.planetbPrefillSuccess = result.prefilledFields.length > 0
      } else if (result.errorMessage) {
        this.$message.warning(result.errorMessage)
      }
    } finally {
      this.planetbLoading = false
    }
  }

  handleRgpdConsent(accepted: boolean): void {
    this.showRgpdDialog = false
    if (accepted) {
      this.applyPrefill(this.demoPrefill)
    }
  }

  applyPrefill(data: PartnerPrefillData): void {
    this.form.companyName = data.companyName
    this.form.siret = data.siret
    this.form.siren = data.siren
    this.form.vatNumber = data.vatNumber
    this.form.adminFullName = data.adminFullName
    this.form.adminEmail = data.adminEmail
    this.form.billingAddress = { ...data.billingAddress }
    this.form.deliveryAddresses = data.deliveryAddresses.map(a => ({ ...a }))
    this.prefilledFields = ['companyName', 'siret', 'adminFullName', 'adminEmail']
  }

  previousStep(): void {
    if (this.step > 0) this.step--
  }

  async nextStep(): Promise<void> {
    if (this.step === 0) {
      if (!this.form.acceptedCgu || !this.form.acceptedRgpd) {
        this.legalError = true
        this.$message.warning(this.$t('enrollment.rules.legalRequired') as string)
        setTimeout(() => { this.legalError = false }, 3000)
        return
      }

      const valid = await this.validateForm()
      if (!valid) return
    }

    if (this.step === 1) {
      if (this.needsPhysicalAddressValidation && this.form.physicalDeliveryAddresses.length === 0) {
        this.form.physicalDeliveryAddresses = [createEmptyAddress()]
      }
      if (this.needsPhysicalAddressValidation) {
        const valid = await this.validateForm()
        if (!valid) return
      }
    }

    if (this.step < 2) this.step++
  }

  validateForm(): Promise<boolean> {
    return new Promise(resolve => {
      (this.$refs.enrollmentFormRef as ElForm).validate(valid => resolve(valid))
    })
  }

  async submitEnrollment(): Promise<void> {
    if (this.needsPhysicalAddressValidation && this.form.physicalDeliveryAddresses.length === 0) {
      this.step = 1
      this.form.physicalDeliveryAddresses = [createEmptyAddress()]
      this.$message.warning(this.$t('enrollment.bankCards.physicalAddressesEmpty') as string)
      return
    }

    if (this.needsPhysicalAddressValidation) {
      const valid = await this.validateForm()
      if (!valid) {
        this.step = 1
        return
      }
    }

    this.submitting = true
    try {
      // TODO: POST /market/v1/enrollments
      await new Promise(resolve => setTimeout(resolve, 800))

      const siretDigits = this.form.siret.replace(/\s/g, '')
      this.enrollmentResult = {
        carrefourProId: `CP-${siretDigits.slice(0, 9)}`,
        passAvantageId: `PA-${Date.now()}`
      }
      this.successVisible = true
    } finally {
      this.submitting = false
    }
  }
}
